/**
 * Player profile catalogs: avatars (shown in lists / voice chat), cube icons,
 * colours and trails (the look of the in-game character).
 *
 * Ids are stable and stored in players' devices: add new entries freely, but never
 * rename or remove existing ids.
 */

/**
 * Longest player or room name, in UTF-16 units: what the name fields' maxLength counts (an
 * emoji is two), so a name the field takes is never cut again.
 */
export const NAME_MAX_LENGTH = 16;

export const AVATARS = [
  { id: 'cat', emoji: '🐱' },
  { id: 'dog', emoji: '🐶' },
  { id: 'fox', emoji: '🦊' },
  { id: 'panda', emoji: '🐼' },
  { id: 'frog', emoji: '🐸' },
  { id: 'unicorn', emoji: '🦄' },
  { id: 'dragon', emoji: '🐲' },
  { id: 'robot', emoji: '🤖' },
  { id: 'invader', emoji: '👾' },
  { id: 'alien', emoji: '👽' },
  { id: 'dino', emoji: '🦖' },
  { id: 'lion', emoji: '🦁' },
  { id: 'tiger', emoji: '🐯' },
  { id: 'monkey', emoji: '🐵' },
  { id: 'penguin', emoji: '🐧' },
  { id: 'octopus', emoji: '🐙' },
  { id: 'ghost', emoji: '👻' },
  { id: 'rocket', emoji: '🚀' },
  { id: 'bear', emoji: '🐻' },
  { id: 'rabbit', emoji: '🐰' },
  { id: 'koala', emoji: '🐨' },
  { id: 'owl', emoji: '🦉' },
  { id: 'shark', emoji: '🦈' },
  { id: 'pumpkin', emoji: '🎃' },
] as const;
export type AvatarId = (typeof AVATARS)[number]['id'];
export const AVATAR_IDS = AVATARS.map((a) => a.id) as unknown as readonly [AvatarId, ...AvatarId[]];

/** Faces / patterns drawn on the cube (rendered procedurally by the client). */
export const ICONS = [
  { id: 'classic', name: 'Классика' },
  { id: 'smile', name: 'Улыбка' },
  { id: 'cool', name: 'Крутой' },
  { id: 'angry', name: 'Злюка' },
  { id: 'robot', name: 'Робот' },
  { id: 'cat', name: 'Котик' },
  { id: 'star', name: 'Звезда' },
  { id: 'diamond', name: 'Алмаз' },
  { id: 'cross', name: 'Крестик' },
  { id: 'target', name: 'Мишень' },
  { id: 'stripes', name: 'Полоски' },
  { id: 'checker', name: 'Шахматы' },
  { id: 'alien', name: 'Пришелец' },
  { id: 'heart', name: 'Сердечко' },
  { id: 'bolt', name: 'Молния' },
  { id: 'ninja', name: 'Ниндзя' },
  { id: 'dragon', name: 'Дракон' },
  { id: 'wolf', name: 'Волк' },
  // Holiday ones, available all year (old pages draw unknown icons as «Классика»).
  { id: 'pumpkin', name: 'Тыква' },
  { id: 'ghost', name: 'Призрак' },
  { id: 'snowman', name: 'Снеговик' },
] as const;
export type IconId = (typeof ICONS)[number]['id'];
export const ICON_IDS = ICONS.map((i) => i.id) as unknown as readonly [IconId, ...IconId[]];

/** Bright, Geometry-Dash-like palette for the character. */
export const COLORS = [
  { id: 'lime', hex: '#7dff00' },
  { id: 'green', hex: '#00ff5a' },
  { id: 'cyan', hex: '#00ffff' },
  { id: 'sky', hex: '#00c8ff' },
  { id: 'blue', hex: '#2f5bff' },
  { id: 'purple', hex: '#8c00ff' },
  { id: 'magenta', hex: '#ff00ff' },
  { id: 'pink', hex: '#ff77d8' },
  { id: 'red', hex: '#ff2a2a' },
  { id: 'orange', hex: '#ff8c00' },
  { id: 'yellow', hex: '#ffff00' },
  { id: 'gold', hex: '#ffc800' },
  { id: 'white', hex: '#ffffff' },
  { id: 'gray', hex: '#9a9a9a' },
  { id: 'black', hex: '#161616' },
  { id: 'brown', hex: '#8b5a2b' },
] as const;
export type ColorId = (typeof COLORS)[number]['id'];
export const COLOR_IDS = COLORS.map((c) => c.id) as unknown as readonly [ColorId, ...ColorId[]];

/** Effect left behind the character while running. */
export const TRAILS = [
  { id: 'none', name: 'Без следа' },
  { id: 'classic', name: 'Классический' },
  { id: 'sparks', name: 'Искры' },
  { id: 'rainbow', name: 'Радуга' },
  { id: 'bubbles', name: 'Пузыри' },
  { id: 'fire', name: 'Огонь' },
] as const;
export type TrailId = (typeof TRAILS)[number]['id'];
export const TRAIL_IDS = TRAILS.map((t) => t.id) as unknown as readonly [TrailId, ...TrailId[]];

export interface PlayerSkin {
  icon: IconId;
  primary: ColorId;
  secondary: ColorId;
  trail: TrailId;
}

export interface PlayerProfile {
  name: string;
  avatar: AvatarId;
  skin: PlayerSkin;
}

export function colorHex(id: ColorId): string {
  return COLORS.find((c) => c.id === id)?.hex ?? '#ffffff';
}

export function avatarEmoji(id: AvatarId): string {
  return AVATARS.find((a) => a.id === id)?.emoji ?? '🙂';
}

// Control characters, zero-width/bidi marks and line/paragraph separators (written as
// escapes on purpose: never paste these characters literally into source files). The zero
// width joiner is not among them: emoji sequences need it (see sanitizeName).
const CONTROL_AND_INVISIBLE_CHARS =
  // biome-ignore lint/suspicious/noControlCharactersInRegex: matching control characters is the point
  /[\u0000-\u001f\u007f-\u009f\u200b\u200c\u200e\u200f\u2028-\u202e\u2066-\u2069\ufeff]/g;

const ZERO_WIDTH_JOINER = '\u200d';
const EMOJI_VARIATION = '\ufe0f';

/**
 * Pictographs a joiner glues into one emoji (woman + joiner + rocket is the astronaut, cat +
 * joiner + black square the black cat): the emoji blocks, then arrows, symbols, dingbats and
 * shapes (the female sign, the heart, the black square). Ranges, not
 * \p{Extended_Pictographic}: every JavaScript engine reads them.
 */
function isPictograph(char: string | undefined): boolean {
  const code = char?.codePointAt(0) ?? 0;
  return (code >= 0x1f000 && code <= 0x1faff) || (code >= 0x2190 && code <= 0x2bff);
}

/** A joiner belongs between two pictographs (the first may end in a skin tone or U+FE0F). */
function joinsEmoji(before: string | undefined, after: string | undefined): boolean {
  return (isPictograph(before) || before === EMOJI_VARIATION) && isPictograph(after);
}

/**
 * Trims, collapses whitespace, strips control and invisible characters (a zero width joiner
 * stays only inside an emoji: between letters it would just hide in the name) and cuts the
 * name to NAME_MAX_LENGTH UTF-16 units, never inside a character.
 */
export function sanitizeName(raw: string): string {
  const chars = Array.from(
    raw.replace(CONTROL_AND_INVISIBLE_CHARS, '').replace(/\s+/g, ' ').trim(),
  );
  let name = '';
  for (const [i, char] of chars.entries()) {
    if (char === ZERO_WIDTH_JOINER && !joinsEmoji(chars[i - 1], chars[i + 1])) continue;
    if (name.length + char.length > NAME_MAX_LENGTH) break;
    name += char;
  }
  // Cut right after a joiner: it joins nothing now.
  return (name.endsWith(ZERO_WIDTH_JOINER) ? name.slice(0, -1) : name).trim();
}

/** A random but pleasant-looking profile; `random` returns floats in [0, 1). */
export function randomProfile(name: string, random: () => number = Math.random): PlayerProfile {
  const pick = <T>(items: readonly T[]): T => items[Math.floor(random() * items.length)] as T;
  const primary = pick(COLOR_IDS);
  const secondary = pick(COLOR_IDS.filter((c) => c !== primary));
  return {
    name: sanitizeName(name),
    avatar: pick(AVATAR_IDS),
    skin: { icon: pick(ICON_IDS), primary, secondary, trail: 'classic' },
  };
}
